import type {
  CreateDataSyncTaskRequest,
  CreateDataSyncTaskResponse,
} from './validations/types';

import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { vestigasApiNoConversion } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { omitUnsupportedDeactivationConfig } from './deactivationConfig';
import { queryKeysDataSyncJob } from './queryKeys';
import {
  parseDataSyncResponse,
  rawEntityResponseSchema,
} from './responseShapes';
import { toApiKeepingUserKeys } from './userKeyedFields';
import { createDataSyncTaskSchema } from './validations';
/**
 * Creates a new data sync task.
 */
export const createDataSyncTask = async (
  data: CreateDataSyncTaskRequest,
): Promise<CreateDataSyncTaskResponse> => {
  // Decode: Transform UI format (array) → API format (object), then drop
  // deactivationConfig for entity types the backend does not apply it to.
  const validatedData = omitUnsupportedDeactivationConfig(
    createDataSyncTaskSchema.parse(data),
  );

  const rawResponse = await vestigasApiNoConversion
    .post(ENDPOINT.DATA_SYNC.CREATE_TASK(), {
      json: toApiKeepingUserKeys(validatedData),
    })
    .json<unknown>();

  return parseDataSyncResponse(
    rawEntityResponseSchema,
    rawResponse,
    'task creation',
  );
};

/**
 * React Query mutation hook for creating a data sync task.
 * Automatically invalidates the jobs list query on success.
 *
 * @param options - Additional mutation options
 */
export const useMutationCreateTask = (
  options?: Omit<
    UseMutationOptions<
      CreateDataSyncTaskResponse,
      Error,
      CreateDataSyncTaskRequest
    >,
    'mutationFn'
  >,
) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    ...options,
    mutationFn: createDataSyncTask,
    async onSuccess() {
      // Invalidate all data sync queries to refetch
      await queryClient.invalidateQueries({
        queryKey: queryKeysDataSyncJob.base(),
      });

      ToastService.success('Sync-Task erfolgreich erstellt');
    },
  });

  return mutation;
};
